import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Snackbar, Alert, Chip, IconButton, Button
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { getUserRights, updateUserRight } from '../../api/roleRightApi';
import { getAgentDetailsByAgentId } from '../../api/userApi';
import handleAgentName from '../../utils/userUtils';

export default function UserPermissions() {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [rights, setRights] = useState([]);
    const [userInfo, setUserInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const fetchData = useCallback(async () => {
        try {
            const [rightsRes, userRes] = await Promise.all([
                getUserRights(userId),
                getAgentDetailsByAgentId(userId),
            ]);
            setRights(rightsRes.data);
            setUserInfo(userRes.data);
        } catch (err) {
            console.error('Error fetching user permissions:', err);
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleToggle = async (rightId, action) => {
        try {
            if (action === 'reset') {
                await updateUserRight({ userId: parseInt(userId), rightId, isGranted: false, remove: true });
            } else {
                await updateUserRight({ userId: parseInt(userId), rightId, isGranted: action === 'grant', remove: false });
            }
            setSnackbar({ open: true, message: 'Permission updated', severity: 'success' });
            fetchData();
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to update', severity: 'error' });
        }
    };

    const headCellSx = {
        fontSize: '0.75rem', fontWeight: 600, color: '#64748B',
        fontFamily: "'Poppins', sans-serif", padding: '10px 14px',
        backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0',
    };

    const bodyCellSx = {
        fontSize: '0.8rem', color: '#374151', fontFamily: "'Poppins', sans-serif",
        padding: '8px 14px', borderBottom: '1px solid #F1F5F9',
    };

    if (loading) return <div>Loading...</div>;

    return (
        <div style={{ padding: 24, maxWidth: 900, margin: '0 auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <IconButton onClick={() => navigate(-1)}><ArrowBackIcon /></IconButton>
                <div>
                    <Typography variant="h5" fontWeight={700} fontFamily="'Poppins', sans-serif">
                        Permissions — {handleAgentName(userInfo?.firstName, userInfo?.lastName, userInfo?.alias)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" fontFamily="'Poppins', sans-serif">
                        Override role defaults for this user. Changes take effect on next login.
                    </Typography>
                </div>
            </Box>

            <TableContainer sx={{ backgroundColor: '#fff', borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ ...headCellSx, minWidth: 200 }}>Right</TableCell>
                            <TableCell sx={{ ...headCellSx, textAlign: 'center', width: 100 }}>From Role</TableCell>
                            <TableCell sx={{ ...headCellSx, textAlign: 'center', width: 120 }}>Override</TableCell>
                            <TableCell sx={{ ...headCellSx, textAlign: 'center', width: 100 }}>Effective</TableCell>
                            <TableCell sx={{ ...headCellSx, textAlign: 'center', width: 200 }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rights.map(r => (
                            <TableRow key={r.rightId} sx={{ '&:hover': { backgroundColor: '#F8FAFC' } }}>
                                <TableCell sx={bodyCellSx}>
                                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, fontFamily: "'Poppins', sans-serif" }}>
                                        {r.rightName}
                                    </Typography>
                                    {r.rightDescription && (
                                        <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8', fontFamily: "'Poppins', sans-serif" }}>
                                            {r.rightDescription}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
                                    {r.fromRole ? (
                                        <Chip label="Yes" size="small" sx={{ fontSize: 10, bgcolor: '#DBEAFE', color: '#1A8CFF' }} />
                                    ) : (
                                        <Chip label="No" size="small" sx={{ fontSize: 10, bgcolor: '#F1F5F9', color: '#94A3B8' }} />
                                    )}
                                </TableCell>
                                <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
                                    {r.userOverride === null ? (
                                        <Chip label="Default" size="small" sx={{ fontSize: 10, bgcolor: '#F1F5F9', color: '#94A3B8' }} />
                                    ) : r.userOverride ? (
                                        <Chip label="Granted" size="small" sx={{ fontSize: 10, bgcolor: '#D1FAE5', color: '#059669' }} />
                                    ) : (
                                        <Chip label="Revoked" size="small" sx={{ fontSize: 10, bgcolor: '#FEE2E2', color: '#DC2626' }} />
                                    )}
                                </TableCell>
                                <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
                                    {r.effectiveAccess ? (
                                        <CheckCircleIcon sx={{ color: '#10B981', fontSize: 20 }} />
                                    ) : (
                                        <CancelIcon sx={{ color: '#EF4444', fontSize: 20 }} />
                                    )}
                                </TableCell>
                                <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
                                    <Button
                                        size="small"
                                        variant={r.userOverride === true ? 'contained' : 'outlined'}
                                        color="success"
                                        onClick={() => handleToggle(r.rightId, 'grant')}
                                        sx={{ minWidth: 50, fontSize: 10, mr: 0.5, textTransform: 'none' }}
                                    >
                                        Grant
                                    </Button>
                                    <Button
                                        size="small"
                                        variant={r.userOverride === false ? 'contained' : 'outlined'}
                                        color="error"
                                        onClick={() => handleToggle(r.rightId, 'revoke')}
                                        sx={{ minWidth: 50, fontSize: 10, mr: 0.5, textTransform: 'none' }}
                                    >
                                        Revoke
                                    </Button>
                                    {r.userOverride !== null && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="inherit"
                                            onClick={() => handleToggle(r.rightId, 'reset')}
                                            sx={{ minWidth: 50, fontSize: 10, textTransform: 'none' }}
                                        >
                                            Reset
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={2000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity} variant="filled" sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.8rem' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
}
